<script setup lang="ts">
import { computed } from 'vue';
import UiText from './UiText.vue';
import UiIcon, { type UiIconProps } from './UiIcon.vue';
import UiTooltip from './UiTooltip.vue';
import UiConditionalWrapper from './UiConditionalWrapper.vue';

interface UiChipProps {
  text: string;
  variant?: 'default' | 'primary' | 'highlight' | 'success' | 'dark' | 'warn' | 'neutral' | 'outline' | 'plain';
  rounded?: 'normal' | 'full' | 'small';
  iconAfter?: boolean;
  size?: 'xs' | 'sm' | 'md' | 'compact' | 'tag';
  icon?: UiIconProps['name'];
  tooltip?: string;
  textClass?: Record<string, boolean>;
  /** Override the variant's background color (e.g. `bg-violet-100`). */
  bgColorClass?: string;
  /** Override the variant's foreground/text color (e.g. `text-violet-700`). */
  textColorClass?: string;
}

const props = withDefaults(defineProps<UiChipProps>(), {
  variant: 'default',
  rounded: 'normal',
  size: 'md',
  iconAfter: false,
  theme: 'light',
});

interface VariantStyle {
  bg: string;
  text: string;
  extras: string;
}

const variantStyle = computed<VariantStyle>(() => {
  switch (props.variant) {
    case 'primary':
      return { bg: 'bg-primary', text: 'text-primary-foreground', extras: 'hover:bg-primary/80' };
    case 'highlight':
      return { bg: '', text: 'text-se-highlight', extras: 'border-se-highlight border' };
    case 'success':
      return { bg: 'bg-se-success-light', text: 'text-se-dark', extras: 'border-se-success border' };
    case 'warn':
      return { bg: 'bg-se-warn-light', text: 'text-se-warn', extras: '' };
    case 'neutral':
      return { bg: 'bg-se-gray-200', text: 'text-se-dark', extras: 'border border-se-gray-300' };
    case 'outline':
      return { bg: 'bg-se-white', text: 'text-se-dark', extras: 'border border-se-gray-300' };
    case 'dark':
      return { bg: 'bg-se-dark', text: 'text-se-white', extras: 'border border-se-white' };
    case 'plain':
      return { bg: '', text: '', extras: '' };
    default:
      return { bg: 'bg-white', text: 'text-gray-900', extras: 'border border-gray-200 hover:bg-gray-50' };
  }
});

const chipClasses = computed(() => {
  const { bg, text, extras } = variantStyle.value;
  const bgClass = props.bgColorClass ?? bg;
  const textClass = props.textColorClass ?? text;
  return [bgClass, textClass, extras].filter(Boolean).join(' ');
});

const textVariant = computed(() => {
  switch (props.size) {
    case 'xs':
      return 'caption-normal';
    case 'sm':
      return 'h6';
    case 'compact':
      return 'override';
    case 'tag':
      return 'override';
    default:
      return 'p';
  }
});
</script>

<template>
  <div class="inline-flex max-w-full min-w-0">
    <UiConditionalWrapper :condition="!!props.tooltip" :wrapper="UiTooltip" :wrapper-props="{ content: props.tooltip }">
      <span class="inline-flex items-center justify-center cursor-pointer whitespace-nowrap max-w-full" :class="{
        [chipClasses]: true,
        'rounded-full': props.rounded === 'full' && props.size !== 'tag',
        'rounded-md': props.rounded === 'normal' && props.size !== 'tag',
        'rounded-sm': (props.rounded === 'small' && props.size !== 'tag') || props.size === 'tag',
      }">
        <slot name="before" />
        <div class="flex items-center min-w-0 max-w-full overflow-hidden" :class="{
          'px-3 py-1 gap-1': props.size === 'md',
          'px-1.5 py-0 gap-1': props.size === 'xs',
          'px-3 py-0 gap-1': props.size === 'sm',
          'px-2 py-0.5 gap-1': props.size === 'compact',
          'px-1.5 py-0.5 gap-1': props.size === 'tag',
          ...(props.textClass || {}),
        }">
          <slot name="inline-before" />
          <UiText as="span" v-if="props.iconAfter" :text="text" :variant="textVariant" no-wrap :class="{
            'truncate min-w-0': true,
            'text-base leading-6 font-normal': props.size === 'compact',
            'text-tooltip font-semibold': props.size === 'tag',
          }" />
          <UiIcon :name="props.icon" v-if="props.icon" class="shrink-0" :class="{
            'w-4 h-4': props.size === 'md' || props.size === 'sm',
            'w-3 h-3': props.size === 'xs' || props.size === 'compact' || props.size === 'tag',
            '-ml-1': props.icon === 'close',
          }" />
          <UiText as="span" v-if="!props.iconAfter" :text="text" :variant="textVariant" no-wrap :class="{
            'truncate min-w-0': true,
            'text-base leading-6 font-normal': props.size === 'compact',
            'text-tooltip font-semibold': props.size === 'tag',
          }" />
          <slot name="inline-after" />
        </div>
        <slot name="after" />
      </span>
    </UiConditionalWrapper>
  </div>
</template>
